import { BufferGeometry, Group, Material, Mesh, Object3D } from 'three';
import { v4 } from 'uuid';

import { t } from '../../../../../utils/i18-next';
import { isWireframe } from '../../../../../utils/three/mesh';
import { isDefined } from '../../../../../utils/variables';
import { AssetVisibilityManager } from '../../../../../viewer/asset-visibility-manager';
import { Design } from '../../../../../viewer/elements/design';
import { Dxf } from '../../../../../viewer/elements/dxf';
import { ElementObj } from '../../../../../viewer/elements/element-obj';
import { Geometry } from '../../../../../viewer/elements/geometry';
import { Glb } from '../../../../../viewer/elements/glb';
import { TerrainModel } from '../../../../../viewer/elements/terrain-model';
import { VolumeCalculationAsset } from '../../../../../viewer/elements/volume/types';
import { ProjectGeodataManager } from '../../../../../viewer/project-geodata-manager';
import { Tool } from '../../../../../viewer/tools/tool';

export enum VolumeCalculationType {
  Area,
  Design,
  PointCloud,
  Dxf,
  Glb,
  TerrainModel,
}

/**
 * This limited is based on what currently is the largest stable calculation
 * we have run in prod.
 *
 * Note: for reference the largest volume we have currently caluclated
 * in prod was 250 x 74 thousand triangles.
 * This resulted in a ~300 mb glb model and took 50 seconds to generate.
 *
 * that equals to about 18500000000 iterations.
 *
 * a 30000000000 iteration limit is approximately 1.6 times that size.
 *
 * NOTE: Make sure to update this in backend too if changed.
 */
const iterationLimit = 30000000000;

export type VolumeCalculationMethod = 'cell_grid' | 'prism';

export const translateVolumeCalculationType = (type: VolumeCalculationType): string => {
  switch (type) {
    case VolumeCalculationType.Area:
      return t('type.area', { ns: 'skyviewVolumeCalculation' });
    case VolumeCalculationType.Design:
      return t('type.design', { ns: 'skyviewVolumeCalculation' });
    case VolumeCalculationType.Dxf:
      return t('type.dxf', { ns: 'skyviewVolumeCalculation' });
    case VolumeCalculationType.Glb:
      return t('type.glb', { ns: 'skyviewVolumeCalculation' });
    case VolumeCalculationType.PointCloud:
      return t('type.pointCloud', { ns: 'skyviewVolumeCalculation' });
    case VolumeCalculationType.TerrainModel:
      return t('type.terrainModel', { ns: 'skyviewVolumeCalculation' });
  }
};

export type VolumeCalculationOption = {
  displayName: string;
  volumeCalculationDataType: VolumeCalculationType;
  pointCloudId?: string;
  sourceTerrainModelGeoDataId?: string;
  pointCloudContainerIndex?: number;
  triangleCount?: number;
  asset?: VolumeCalculationAsset;
  meshes?: Mesh[];
  id: string;
};

export interface VolumeCalculationInfo {
  triangles: number | undefined;
  meshes?: Mesh[];
}

export enum BoundaryCalculationType {
  POLYGON,
  AREA,
  CAD,
  NONE,
}

const findVolumeCalculationOption = (
  options: VolumeCalculationOption[] | undefined,
  id: string | undefined,
): VolumeCalculationOption | undefined => {
  if (id && options) {
    return options.find((option) => option.id === id);
  }
};

const findVolumeCalculationOptionByAssetId = (
  options: VolumeCalculationOption[] | undefined,
  assetId: string | undefined,
): VolumeCalculationOption | undefined => {
  if (assetId && options) {
    return options.find((option) => option.asset?.assetId === assetId);
  }
};

/**
 * Check uniqueness of the asset option ids
 */
const validateUniqueness = (options: VolumeCalculationOption[]) => {
  const seenIds = new Set<string>();
  options.forEach((option) => {
    if (!isDefined(option.id)) {
      throw new Error(`Data option id is undefined: ${JSON.stringify(option)}`);
    }
    if (seenIds.has(option.id)) {
      throw new Error(`Duplicated data option id is detected: ${option.id}`);
    }
    seenIds.add(option.id);
  });
};

/**
 * Calculates the sum of all triangles in an array of meshes.
 * Adjusted to work for both indexed and non-indexed meshes.
 * @param meshes
 * @returns
 */
export const countTriangles = (meshes: Mesh[]) => {
  return meshes.reduce((prev, next) => {
    const geometry = next.geometry;
    return prev + (geometry.index?.count || geometry.attributes.position.count || 0) / 3;
  }, 0);
};

/**
 * Extracts the mesh from the dxf. If it's already loaded into the scene it will use to loaded data.
 * If it's not loaded into the scene it will parse the dxf and extract the mesh information.
 */
export const getMeshDataDxf = (dxf: Object3D) => {
  const meshes: Mesh[] = [];
  dxf.traverse((child: Object3D) => {
    if (
      child instanceof Mesh &&
      child.geometry instanceof BufferGeometry &&
      child.name === 'Surface'
    ) {
      meshes.push(child as Mesh);
    }
  });

  const triangleCount = countTriangles(meshes);

  return { meshes, triangleCount };
};

const loadGeometryAsset = async (data: VolumeCalculationOption) => {
  if (!(data?.asset instanceof Geometry)) {
    throw new Error('Option has no geometry assets');
  }

  if (!data.asset.areaMesh) {
    data.asset.loadGeometry();
  }

  if (data.asset.areaMesh) {
    data.meshes = [data.asset.areaMesh];
    data.triangleCount = countTriangles([data.asset.areaMesh]);
  }

  if (!data.asset.getStateVisibility()) {
    await AssetVisibilityManager.getInstance().toggleAsset(data.asset.uuid, true, false);
  }

  return data;
};

const loadDxfAsset = async (data: VolumeCalculationOption) => {
  if (!(data?.asset instanceof Dxf)) {
    throw new Error('Option has no Dxf assets');
  }

  // Need to enabled parse when working with DXF-files.
  // Not really sure of its usa,
  // but might have to do something about not loading CAD-files when the viewer loads.
  data.asset.parseEnabled = true;

  data.asset.visible = true;
  await data.asset.parse();

  await AssetVisibilityManager.getInstance().toggleAsset(data.asset.uuid, true, false);

  const meshData = getMeshDataDxf(data.asset);
  data.meshes = meshData.meshes;
  data.triangleCount = meshData.triangleCount ?? 0;
  return data;
};

const loadTerrainModelAsset = async (data: VolumeCalculationOption) => {
  if (!(data?.asset instanceof TerrainModel)) {
    throw new Error('Option has no TerrainModel assets');
  }

  await data.asset.loadModelData();
  await AssetVisibilityManager.getInstance().toggleAsset(data.asset.uuid, true, false);

  const meshes: Mesh[] = [data.asset.glbData!];
  data.meshes = meshes;
  data.triangleCount = countTriangles(meshes);
  return data;
};

const loadGlbAsset = async (data: VolumeCalculationOption) => {
  if (!(data?.asset instanceof Glb)) {
    throw new Error('Option has no Glb assets');
  }

  await AssetVisibilityManager.getInstance().toggleAsset(data.asset.uuid, true, false);

  const countGLBTrianglesInfo = countGLBTriangles(data.asset.glbData);

  data.meshes = countGLBTrianglesInfo.meshes;
  data.triangleCount = countGLBTrianglesInfo.countTriangles;
  return data;
};

const loadDesignAsset = async (data: VolumeCalculationOption) => {
  if (!(data?.asset instanceof Design)) {
    throw new Error('Option has no design assets');
  }

  if (data.asset.loadingStatus !== 'loaded') {
    await data.asset.showModel();
  }

  await AssetVisibilityManager.getInstance().toggleAsset(data.asset.uuid, true, false);

  if (data.asset.designNode.faceNodeCount > 0) {
    data.meshes = [data.asset.designNode.data.faces];
    data.triangleCount = data.asset.designNode.faceNodeCount;
  } else {
    data.meshes = [];
    data.triangleCount = 0;
  }
  return data;
};

export const countGLBTriangles = (glbData: Object3D | Group | undefined) => {
  const meshes: Mesh[] = [];

  glbData?.traverse((node) => {
    if (node instanceof Mesh && node.geometry instanceof BufferGeometry && !isWireframe(node)) {
      meshes.push(node as Mesh<BufferGeometry, Material>);
    }
  });
  return { countTriangles: countTriangles(meshes), meshes };
};

const getAssetsValidForVolumeCalculation = (assets: ElementObj[]) => {
  const dataOptions: VolumeCalculationOption[] = [];
  for (const asset of assets) {
    if (asset instanceof Geometry && asset.isArea) {
      dataOptions.push({
        displayName: `${asset.name} (${translateVolumeCalculationType(VolumeCalculationType.Area)})`,
        volumeCalculationDataType: VolumeCalculationType.Area,
        id: v4(),
        asset,
      });
    } else if (asset instanceof Design) {
      dataOptions.push({
        displayName: `${asset.name} (${translateVolumeCalculationType(VolumeCalculationType.Design)})`,
        volumeCalculationDataType: VolumeCalculationType.Design,
        id: v4(),
        asset,
      });
    } else if (asset instanceof Dxf) {
      dataOptions.push({
        displayName: `${asset.name} (${translateVolumeCalculationType(VolumeCalculationType.Glb)})`,
        volumeCalculationDataType: VolumeCalculationType.Dxf,
        id: v4(),
        asset,
      });
    } else if (asset instanceof Glb && asset.getFileType() !== 'Geo') {
      dataOptions.push({
        displayName: `${asset.name} (${translateVolumeCalculationType(VolumeCalculationType.Glb)})`,
        volumeCalculationDataType: VolumeCalculationType.Glb,
        id: v4(),
        asset,
      });
    } else if (asset instanceof TerrainModel) {
      dataOptions.push({
        displayName: `${asset.name} (${translateVolumeCalculationType(VolumeCalculationType.TerrainModel)})`,
        volumeCalculationDataType: VolumeCalculationType.TerrainModel,
        sourceTerrainModelGeoDataId: asset.geoDataId,
        id: v4(),
        asset,
      });
    }
  }
  return dataOptions;
};

const sortVolumeCalculationDataOptions = (options: VolumeCalculationOption[]) => {
  options.sort((a, b) => {
    const typeValue = (value: VolumeCalculationType) => {
      switch (value) {
        case VolumeCalculationType.PointCloud:
          return 0;
        case VolumeCalculationType.Design:
          return 1;
        case VolumeCalculationType.Area:
          return 2;
        case VolumeCalculationType.Dxf:
        case VolumeCalculationType.Glb:
          return 3;
        case VolumeCalculationType.TerrainModel:
          return 4;
      }
    };

    const type1 = typeValue(a.volumeCalculationDataType);
    const type2 = typeValue(b.volumeCalculationDataType);

    if (type1 === type2) {
      if (a.pointCloudContainerIndex !== undefined && b.pointCloudContainerIndex !== undefined) {
        return a.pointCloudContainerIndex < b.pointCloudContainerIndex ? 1 : -1;
      }

      return a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1 : -1;
    }

    return type1 > type2 ? 1 : -1;
  });
};

const loadTargetDataOptions = (assets: ElementObj[]) => {
  const targetDataOptions = getAssetsValidForVolumeCalculation(assets);

  sortVolumeCalculationDataOptions(targetDataOptions);
  return targetDataOptions;
};

const loadSourceDataOptions = (
  setSelectedSource: (option: VolumeCalculationOption) => void,
  assets: ElementObj[],
) => {
  const sourceDataOptions: VolumeCalculationOption[] = [];

  for (const container of ProjectGeodataManager.instance().getTimelineContainers()) {
    const activeTimeline = ProjectGeodataManager.instance().getActiveTimelineContainer()!;
    if (container.pointCloud) {
      const pointCloudOption: VolumeCalculationOption = {
        displayName: `${t('terrainModel.timeline', { ns: 'skyviewAssetMenu' })} ${container.containerIndex.toString()} (${t('geodata.artifacts.pointCloud', { ns: 'common' })} ${
          container.displayDate
        })`,
        id: v4(),
        volumeCalculationDataType: VolumeCalculationType.PointCloud,
        pointCloudId: container.pointCloud,
        pointCloudContainerIndex: container.containerIndex,
      };

      if (container.id === activeTimeline.id) {
        pointCloudOption.displayName =
          pointCloudOption.displayName + ` (${t('timeline.current', { ns: 'skyview' })})`;
        setSelectedSource(pointCloudOption);
      }

      sourceDataOptions.push(pointCloudOption);
    }
  }

  sourceDataOptions.push(...getAssetsValidForVolumeCalculation(assets));
  sortVolumeCalculationDataOptions(sourceDataOptions);
  return sourceDataOptions;
};

const updatePointCloud = (data: VolumeCalculationOption) => {
  data.triangleCount = 0;
};

const loadVolumeCalculationOption = async (
  volumeOption: VolumeCalculationOption | undefined,
  callback: (option: VolumeCalculationOption | undefined) => void,
) => {
  if (!isDefined(volumeOption)) {
    callback(undefined);
    return;
  }

  if (volumeOption?.asset instanceof Geometry) {
    await loadGeometryAsset(volumeOption);
  } else if (volumeOption?.asset instanceof Dxf) {
    await loadDxfAsset(volumeOption);
  } else if (volumeOption?.asset instanceof TerrainModel) {
    await loadTerrainModelAsset(volumeOption);
  } else if (volumeOption?.asset instanceof Glb) {
    await loadGlbAsset(volumeOption);
  } else if (volumeOption?.asset instanceof Design) {
    await loadDesignAsset(volumeOption);
  } else if (volumeOption.pointCloudId) {
    updatePointCloud(volumeOption);
  }

  callback(volumeOption);
  return volumeOption;
};

/**
 *  Returns all available dxf assets.
 */
const loadValidCadAssets = (
  selectedTarget: VolumeCalculationOption | undefined,
  assets: ElementObj[],
) => {
  const cadAssets = assets
    .filter((a) => a instanceof Dxf)
    .filter((dxf) => dxf.assetId !== selectedTarget?.asset?.assetId);
  return cadAssets;
};

/**
 *  Returns all available area assets that is not the selected asset.
 */
const loadValidAreaAssets = (
  selectedTarget: VolumeCalculationOption | undefined,
  assets: ElementObj[],
) => {
  const areaAssets = assets
    .filter((a) => a instanceof Geometry && a.isArea)
    .filter((area) => area.assetId !== selectedTarget?.asset?.assetId);
  return areaAssets;
};

export const volumeCalculationHelper = {
  loadValidAreaAssets,
  loadValidCadAssets,
  loadSourceDataOptions,
  loadTargetDataOptions,
  findVolumeCalculationOptionByAssetId,
  findVolumeCalculationOption,
  loadVolumeCalculationOption,
  validateUniqueness,

  checks: {
    isPrismVolumeTowardsPointCloud,
    isPrismIndexLimitExceeded,
    isPrismVolumeTowardsTerrainModel,
    isPrismWithBoundary,
    areVolumeCalculationAssetsTheSame,
    isVolumeCalculationDataValid,
    isBoundarySelectionValid,
  },
};

/**
 * Checks that the calculation is loaded and valid for calculation
 */
function isVolumeCalculationDataValid(
  selectedOption?: VolumeCalculationOption,
  selectedOptionInfo?: VolumeCalculationInfo,
): boolean {
  if (selectedOption?.volumeCalculationDataType === VolumeCalculationType.PointCloud) {
    return isDefined(selectedOption);
  }

  return (
    isDefined(selectedOption) &&
    isDefined(selectedOption.asset) &&
    isDefined(selectedOptionInfo) &&
    isDefined(selectedOptionInfo.triangles) &&
    isDefined(selectedOptionInfo.meshes) &&
    selectedOptionInfo.meshes.length > 0
  );
}

function isBoundarySelectionValid(
  boundaryCalculationType: BoundaryCalculationType,
  activeTool: Tool,
  polygonVolumeIsValid: boolean,
  selectedBoundaryAsset?: VolumeCalculationAsset,
) {
  if (boundaryCalculationType === BoundaryCalculationType.NONE) {
    return true;
  }

  if (activeTool && boundaryCalculationType === BoundaryCalculationType.POLYGON) {
    return polygonVolumeIsValid;
  }

  return isDefined(selectedBoundaryAsset);
}

/**
 * Prism volume calculations do currently not support calculating to or from terrain models.
 */
function isPrismVolumeTowardsTerrainModel(
  calculationMethod: VolumeCalculationMethod,
  selectedTarget: VolumeCalculationOption | undefined,
  selectedSource: VolumeCalculationOption | undefined,
) {
  return (
    calculationMethod === 'prism' &&
    (isDefined(selectedTarget?.sourceTerrainModelGeoDataId) ||
      isDefined(selectedSource?.sourceTerrainModelGeoDataId))
  );
}

/**
 * Prism volume calculations do currently not support calculating to or from point clouds.
 */
function isPrismVolumeTowardsPointCloud(
  calculationMethod: VolumeCalculationMethod,
  selectedTarget: VolumeCalculationOption | undefined,
  selectedSource: VolumeCalculationOption | undefined,
) {
  return (
    calculationMethod === 'prism' &&
    (isDefined(selectedTarget?.pointCloudId) || isDefined(selectedSource?.pointCloudId))
  );
}

/**
 * Prism volume has a combined index limit based on how many iterations the calculation will
 * result in, this is simply calculated as target triangle count * source triangle count.
 */
function isPrismIndexLimitExceeded(
  calculationMethod: VolumeCalculationMethod,
  selectedTarget: VolumeCalculationOption | undefined,
  selectedSource: VolumeCalculationOption | undefined,
) {
  return (
    calculationMethod === 'prism' &&
    (selectedTarget?.triangleCount ?? 0) * (selectedSource?.triangleCount ?? 0) > iterationLimit
  );
}

/**
 * Prism volume calculations do currently not support boundary polygons.
 */
function isPrismWithBoundary(
  boundaryCalculationType: BoundaryCalculationType,
  calculationMethod: VolumeCalculationMethod,
) {
  return calculationMethod === 'prism' && boundaryCalculationType !== BoundaryCalculationType.NONE;
}

/**
 * An asset cannot be calculated towards itself.
 */
function areVolumeCalculationAssetsTheSame(
  selectedTarget: VolumeCalculationOption | undefined,
  selectedSource: VolumeCalculationOption | undefined,
) {
  return (
    isDefined(selectedTarget?.asset) && selectedTarget?.asset?.uuid === selectedSource?.asset?.uuid
  );
}
